/**
 * ConnectCrmImportDialog — multi-step modal launched from the super-admin
 * Accounts panel. Queues a Connect CRM import job for an impersonated user
 * by:
 *   1. Form: pick workspace, enter Connect email + password.
 *   2. (conditional) Idempotency conflict: prior import found — Cancel,
 *      Merge new contacts, or Replace all.
 *   3. Success: confirmation + auto-close.
 *
 * No actual extraction happens here. The RPC `start_connect_crm_import`
 * inserts a queued `import_batches` row + an encrypted credential row that
 * PR-3's worker will pick up. Test Connection is intentionally stubbed —
 * it lights up in PR-3 once the /test-connection edge function ships.
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { Loader2, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';

import { useToast } from '@/hooks/use-toast';
import { useStartConnectCrmImport } from '@/hooks/useStartConnectCrmImport';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import type {
  AdminUserSummary,
  AdminUserWorkspace,
  StartConnectCrmImportResult,
} from '@/types/admin';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
const formSchema = z.object({
  workspace_id: z.string().uuid('Pick a workspace'),
  connect_email: z
    .string()
    .min(1, 'Connect email is required')
    .email('Must be a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type FormValues = z.infer<typeof formSchema>;

type Step = 'form' | 'conflict' | 'success';

interface ConnectCrmImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUserSummary;
  workspaces: AdminUserWorkspace[];
}

export function ConnectCrmImportDialog({
  open,
  onOpenChange,
  user,
  workspaces,
}: ConnectCrmImportDialogProps): JSX.Element {
  const { toast } = useToast();
  const startImport = useStartConnectCrmImport();

  const [step, setStep] = useState<Step>('form');
  const [conflict, setConflict] = useState<StartConnectCrmImportResult | null>(null);
  const [successBatchId, setSuccessBatchId] = useState<string | null>(null);

  // PR-3: Test Connection state. The result lives only for the duration of
  // the form step — switching steps or reopening clears it.
  type TestState =
    | { status: 'idle' }
    | { status: 'pending' }
    | { status: 'success' }
    | { status: 'error'; message: string };
  const [testState, setTestState] = useState<TestState>({ status: 'idle' });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      workspace_id: workspaces[0]?.id ?? '',
      connect_email: '',
      password: '',
    },
  });

  // Reset internal state every time the dialog reopens — stale conflict
  // cards from a previous user would otherwise stick around.
  useEffect(() => {
    if (open) {
      setStep('form');
      setConflict(null);
      setSuccessBatchId(null);
      setTestState({ status: 'idle' });
      form.reset({
        workspace_id: workspaces[0]?.id ?? '',
        connect_email: '',
        password: '',
      });
    }
  }, [open, workspaces, form]);

  // Any change to the credential fields clears a previous test result —
  // a green check would be misleading after the user edits the password.
  const watchedEmail = form.watch('connect_email');
  const watchedPassword = form.watch('password');
  useEffect(() => {
    if (testState.status === 'success' || testState.status === 'error') {
      setTestState({ status: 'idle' });
    }
    // We intentionally don't add testState to deps — that would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedEmail, watchedPassword]);

  /**
   * Probe the Connect CRM credentials. Calls the connect-crm-test-connection
   * edge function (super_admin gated server-side). Result rendered inline; we
   * never block Start Import on a successful test, so the user can skip it
   * if they want.
   */
  const handleTestConnection = async (): Promise<void> => {
    const values = form.getValues();
    if (!values.connect_email?.trim() || !values.password) {
      setTestState({ status: 'error', message: 'Email and password are required' });
      return;
    }
    setTestState({ status: 'pending' });
    try {
      const { data, error } = await supabase.functions.invoke<{ success: boolean; error?: string }>(
        'connect-crm-test-connection',
        {
          body: {
            email: values.connect_email.trim(),
            password: values.password,
          },
        },
      );
      if (error) throw error;
      if (data?.success) {
        setTestState({ status: 'success' });
      } else {
        setTestState({ status: 'error', message: data?.error || 'Login failed' });
      }
    } catch (err) {
      console.error('Test connection failed:', err);
      captureError(err as Error, { feature: 'connect-crm-test-connection' });
      const message = err instanceof Error ? err.message : 'Connection test failed';
      setTestState({ status: 'error', message });
    }
  };

  // Auto-close 5s after success.
  useEffect(() => {
    if (step !== 'success') return;
    const t = window.setTimeout(() => {
      onOpenChange(false);
    }, 5000);
    return () => window.clearTimeout(t);
  }, [step, onOpenChange]);

  const submit = async (
    values: FormValues,
    idempotency: 'block' | 'merge' | 'replace' = 'block',
  ) => {
    try {
      const result = await startImport.mutateAsync({
        user_id: user.id,
        workspace_id: values.workspace_id,
        connect_email: values.connect_email.trim(),
        password: values.password,
        idempotency,
      });

      if (result.conflict) {
        setConflict(result);
        setStep('conflict');
        return;
      }

      setSuccessBatchId(result.batch_id ?? null);
      setStep('success');
      toast({
        title: 'Import queued',
        description: 'The Connect CRM importer will pick this up shortly.',
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to queue import';
      captureError(err as Error, { feature: 'connect-crm-import-start' });
      toast({
        variant: 'destructive',
        title: 'Could not start import',
        description: message,
      });
    }
  };

  const onSubmitForm = form.handleSubmit((values) => submit(values, 'block'));

  const handleMerge = () => {
    void submit(form.getValues(), 'merge');
  };
  const handleReplace = () => {
    void submit(form.getValues(), 'replace');
  };
  const handleCancelConflict = () => {
    setConflict(null);
    setStep('form');
  };

  const isSubmitting = startImport.isPending;
  const displayName =
    [user.first_name, user.last_name].filter(Boolean).join(' ') ||
    user.email ||
    '(unknown user)';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isSubmitting) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import from Connect CRM</DialogTitle>
          <DialogDescription>
            Queue a Connect CRM import for <span className="font-medium">{displayName}</span>.
            The job runs server-side and contacts will appear in the chosen
            workspace once the worker finishes.
          </DialogDescription>
        </DialogHeader>

        {step === 'form' && (
          <Form {...form}>
            <form onSubmit={onSubmitForm} noValidate className="space-y-4">
              <FormField
                control={form.control}
                name="workspace_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Workspace</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isSubmitting || workspaces.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Pick a workspace" />
                        </SelectTrigger>
                        <SelectContent>
                          {workspaces.map((w) => (
                            <SelectItem key={w.id} value={w.id}>
                              <span className="font-medium">{w.name}</span>
                              <span className="ml-2 text-xs text-muted-foreground capitalize">
                                {w.role}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    {workspaces.length === 0 && (
                      <FormDescription className="text-destructive">
                        User has no active workspace memberships — nothing to
                        import into.
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="connect_email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Connect CRM email</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="off"
                        placeholder="agent@example.com"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Connect CRM password</FormLabel>
                    <FormControl>
                      <PasswordInput
                        {...field}
                        autoComplete="off"
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <FormDescription>
                      Stored encrypted at rest. Auto-purged 3 days after the
                      job is queued.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* PR-3: inline test-connection result. Sits above the
                  footer so a long error message wraps naturally. */}
              {testState.status === 'success' && (
                <div className="flex items-center gap-2 text-sm text-emerald-600">
                  <CheckCircle2 className="h-4 w-4" />
                  Connection successful — credentials accepted.
                </div>
              )}
              {testState.status === 'error' && (
                <div className="flex items-start gap-2 text-sm text-destructive">
                  <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{testState.message}</span>
                </div>
              )}

              <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleTestConnection()}
                  disabled={
                    isSubmitting ||
                    testState.status === 'pending' ||
                    !form.watch('connect_email') ||
                    !form.watch('password')
                  }
                >
                  {testState.status === 'pending' && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Test connection
                </Button>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    disabled={isSubmitting || testState.status === 'pending'}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      isSubmitting ||
                      testState.status === 'pending' ||
                      workspaces.length === 0 ||
                      !form.watch('connect_email') ||
                      !form.watch('password') ||
                      !form.watch('workspace_id')
                    }
                  >
                    {isSubmitting && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Start import
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </Form>
        )}

        {step === 'conflict' && conflict && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center gap-2 space-y-0">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <CardTitle className="text-base">
                  Existing import found
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  Status:{' '}
                  <span className="font-medium capitalize">
                    {conflict.existing_status ?? 'unknown'}
                  </span>
                </div>
                {conflict.existing_completed_at ? (
                  <div className="text-muted-foreground">
                    Last completed:{' '}
                    {formatTimestamp(conflict.existing_completed_at)}
                  </div>
                ) : (
                  <div className="text-muted-foreground">
                    Not completed yet.
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  Choose <span className="font-medium">Merge</span> to add only
                  contacts that aren&apos;t already imported, or{' '}
                  <span className="font-medium">Replace</span> to cancel the
                  prior batch and start fresh.
                </p>
              </CardContent>
            </Card>

            <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCancelConflict}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleMerge}
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Merge new contacts
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleReplace}
                  disabled={isSubmitting || !conflict.can_replace}
                >
                  {isSubmitting && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  Replace all
                </Button>
              </div>
            </DialogFooter>
          </div>
        )}

        {step === 'success' && (
          <div className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center gap-2 space-y-0">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                <CardTitle className="text-base">Import queued</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1">
                <p>
                  The Connect CRM importer will pick this up shortly.
                </p>
                {successBatchId && (
                  <p className="text-xs text-muted-foreground font-mono">
                    Batch {successBatchId}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Track progress in the imports view (coming in PR-4).
                </p>
              </CardContent>
            </Card>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatTimestamp(iso: string): string {
  try {
    return format(new Date(iso), 'PPp'); // e.g. May 7, 2026 4:00 PM
  } catch {
    return iso;
  }
}
